/**
 * V56-fix: Modal obrigatório de seleção de canal para channel_admin_multi.
 * Aparece quando o usuário multicanal não tem canal ativo selecionado.
 * Bloqueia toda a navegação até que um canal seja selecionado.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowLeftRight, Building2, Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useState } from "react";
import { toast } from "sonner";

interface Channel {
  id: number;
  name: string;
  nomeFantasia: string | null;
  logoUrl: string | null;
}

interface ChannelSelectorModalProps {
  open: boolean;
  channels: Channel[];
}

export default function ChannelSelectorModal({ open, channels }: ChannelSelectorModalProps) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const setActiveChannelMut = trpc.auth.setActiveChannel.useMutation({
    onSuccess: () => {
      window.location.reload();
    },
    onError: (err: any) => {
      toast.error(err.message || "Erro ao selecionar canal");
    },
  });

  const handleConfirm = () => {
    if (!selectedId) return;
    setActiveChannelMut.mutate({ channelId: selectedId });
  };

  return (
    <Dialog open={open} onOpenChange={() => {/* Não permite fechar */}}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        // Hide close button
        showCloseButton={false}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5 text-[#002F59]" />
            Selecione o Canal
          </DialogTitle>
          <DialogDescription>
            Você tem acesso a múltiplos canais. Selecione o canal que deseja gerenciar nesta sessão.
            Você poderá trocar de canal a qualquer momento pelo menu do avatar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 mt-4 max-h-[300px] overflow-y-auto">
          {channels.map((ch) => (
            <button
              key={ch.id}
              onClick={() => setSelectedId(ch.id)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                selectedId === ch.id
                  ? "border-[#002F59] bg-[#002F59]/5 ring-2 ring-[#002F59]/20"
                  : "border-border hover:border-[#002F59]/30 hover:bg-muted/50"
              }`}
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#002F59]/10 flex items-center justify-center">
                {ch.logoUrl ? (
                  <img src={ch.logoUrl} alt={ch.name} className="w-8 h-8 rounded-full object-contain" />
                ) : (
                  <Building2 className="h-5 w-5 text-[#002F59]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{ch.nomeFantasia || ch.name}</p>
                {ch.nomeFantasia && ch.nomeFantasia !== ch.name && (
                  <p className="text-xs text-muted-foreground truncate">{ch.name}</p>
                )}
              </div>
              {selectedId === ch.id && (
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[#002F59] flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </button>
          ))}
        </div>

        <div className="mt-4">
          <Button
            onClick={handleConfirm}
            disabled={!selectedId || setActiveChannelMut.isPending}
            className="w-full bg-[#002F59] hover:bg-[#002F59]/90 text-white"
          >
            {setActiveChannelMut.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Selecionando...
              </>
            ) : (
              "Confirmar e Entrar"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
